import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { CommonService } from '../common.service';
import { EmployeeProfile, EmployeeProfileApi } from '../api.gen';
import { AuthService } from '../auth.service';
import { finalize } from 'rxjs/operators';
import { getDateAsUTC } from '../utils';
import { LoaderService } from '../loader.service';

@Injectable({
  providedIn: 'root'
})
export class EmployeeProfileUpdateGuard  {

  employeeProfileId;
  masterData :EmployeeProfile;

  constructor(private router: Router,
              private commonService: CommonService,
              private employeeApi: EmployeeProfileApi,
              private authService: AuthService,
              private loader:LoaderService,){}

  canActivate(
    route: ActivatedRouteSnapshot,state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    this.employeeProfileId = this.authService.ActiveUser.employeeProfileId;
    this.loader.show();
    
    return this.isProfileUpdated().then(isUpdated => {
      this.loader.show();
      if (isUpdated) {
        this.loader.clear();
        return true;
      } else {
        //check if user has already been redirected to userprofile
        if(!sessionStorage.getItem('hasNavigatedToUserProfile')){
         sessionStorage.setItem('hasNavigatedToUserProfile','true')
         this.loader.clear();
         this.router.navigate(['/user']);
         return false;
        }else{
          //if user has already been redirected once (on first time log in), no need to redirect again
          this.loader.clear();
          return true;
        }
      }
    })
  }
  
  private isProfileUpdated(): Promise<Boolean> {
    return this.loadData().then(() => {
      if (this.isPastExpiryDate(this.masterData)) {
        this.commonService.isUserProfileExpiryDatesUpdated.next(false);
        return false;
      }
      this.commonService.isUserProfileExpiryDatesUpdated.next(true);
      return true;
    }).catch(error => {
      console.error("Error loading data:", error);
      return true; // returns true if there is any error in getMasterProfileByProfileId api call
    });
  }
  
  
  loadData(): Promise<void> {
    // Return a promise that resolves when the API call is complete
    return new Promise((resolve, reject) => {
      if (this.employeeProfileId > 0) {
        this.employeeApi.getMasterProfileByProfileId(this.employeeProfileId).pipe(
          finalize(() => {})
        ).subscribe(resp => {
          if (resp) {
            this.masterData = resp;
            resolve();
          }
        }, error => {
          reject(error); // Reject if there's an error in the API call
        });
      } 
      else {
        resolve(); // Resolve if employeeProfileId is not greater than 0
      }
    });
  }   

  isPastExpiryDate(masterData: EmployeeProfile): boolean {
    if (!masterData) {
      return false; // If masterData is null, there are no expiry dates to check.
    }
  
    const currentDateUTC = getDateAsUTC(new Date());
  
    // Helper function to check if a date is past the current date.
    // Returns false if the date is null or not expired.
    const isDateExpired = (dateString) => {
      return dateString && new Date(dateString) < currentDateUTC;
    };
  
    // Check the main expiry dates.
    if (isDateExpired(masterData.passportExpiryDate)) {
      return true;
    }
  
    // Parse details and check expiry dates for children and spouse.
    const checkDependentExpiryDates = (dependentDetails) => {
      if (dependentDetails) {
        const dependents = JSON.parse(dependentDetails).formData;
        return dependents.some(dependent => 
          isDateExpired(dependent.passportExpiryDate) || isDateExpired(dependent.visaExpiryDate)
        );
      }
      return false;
    };
  
    // Check children and spouse expiry dates.
    if (checkDependentExpiryDates(masterData.childrenDetails) || checkDependentExpiryDates(masterData.spouseDetails)) {
      return true;
    }
  
    // If none of the dates are expired, return false.
    return false;
  }
  

}
